import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Save, KeyRound, Mail, UserPlus, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../lib/api";
import { PhoneInput } from "../../components/PhoneInput";
import { SearchableSelect } from "../../components/SearchableSelect";
import { VerifyCodeDialog } from "../../components/VerifyCodeDialog";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";

const LANG_OPTIONS = [
    { value: "en", label: "English" },
    { value: "fr", label: "Français" },
    { value: "ht", label: "Kreyòl Ayisyen" },
];

export default function ProfilePage() {
    const { t, i18n } = useTranslation();
    const { profile, refreshProfile } = useAuth();
    const [f, setF] = useState({});
    const [countries, setCountries] = useState([]);
    const [savingProfile, setSavingProfile] = useState(false);

    useEffect(() => {
        if (profile) setF({ ...profile });
    }, [profile]);

    useEffect(() => {
        api.get("/api/countries").then(({ data }) => setCountries(data || []));
    }, []);

    const save = async () => {
        if (savingProfile) return;
        setSavingProfile(true);
        try {
            await api.put("/api/profile", {
                first_name: f.first_name,
                last_name: f.last_name,
                phone_code: f.phone_code,
                phone: f.phone,
                address: f.address,
                state: f.state,
                zip_code: f.zip_code,
                city: f.city,
                country: f.country,
                preferred_language: f.preferred_language,
                organizer_name: f.organizer_name,
            });
            // Apply language change immediately if it differs
            if (f.preferred_language && f.preferred_language !== i18n.language) {
                await i18n.changeLanguage(f.preferred_language);
            }
            await refreshProfile();
            toast.success(t("profile.saved"));
        } catch (e) {
            toast.error(t("common.error"));
        } finally {
            setSavingProfile(false);
        }
    };

    if (!profile) return <div className="p-10 text-muted-foreground">{t("common.loading")}</div>;

    return (
        <div data-testid="profile-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
            <h1 className="font-heading font-black text-3xl md:text-4xl">{t("profile.title")}</h1>

            <div className="bento-card p-5 md:p-6 space-y-3">
                <h2 className="font-bold text-lg mb-2">{t("profile.title")}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <I label={t("auth.first_name")} v={f.first_name} set={(v) => setF({ ...f, first_name: v })} tid="profile-first-name" />
                    <I label={t("auth.last_name")} v={f.last_name} set={(v) => setF({ ...f, last_name: v })} tid="profile-last-name" />
                    {profile.is_organizer && (
                        <div className="sm:col-span-2">
                            <I label={t("profile.organizer_name")} v={f.organizer_name} set={(v) => setF({ ...f, organizer_name: v })} tid="profile-organizer-name" />
                        </div>
                    )}
                    <PhoneInput
                        label={t("auth.phone")}
                        phoneCode={f.phone_code}
                        phone={f.phone}
                        onChange={({ phone_code, phone }) => setF({ ...f, phone_code, phone })}
                        testPrefix="profile-phone"
                    />
                    <I label={t("auth.address")} v={f.address} set={(v) => setF({ ...f, address: v })} tid="profile-address" />
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("auth.country")}</label>
                        <SearchableSelect
                            testId="profile-country"
                            options={countries.map((c) => ({ value: c.name, label: c.name }))}
                            value={f.country}
                            onValueChange={(v) => setF({ ...f, country: v })}
                            placeholder={t("common.choose")}
                        />
                    </div>
                    <I label={t("auth.state")} v={f.state} set={(v) => setF({ ...f, state: v })} tid="profile-state" />
                    <I label={t("auth.city")} v={f.city} set={(v) => setF({ ...f, city: v })} tid="profile-city" />
                    <I label={t("auth.zip_code")} v={f.zip_code} set={(v) => setF({ ...f, zip_code: v })} tid="profile-zip" />
                    <div>
                        <label className="text-sm font-semibold block mb-1.5">{t("profile.language")}</label>
                        <SearchableSelect
                            testId="profile-language"
                            options={LANG_OPTIONS}
                            value={f.preferred_language || i18n.language}
                            onValueChange={(v) => setF({ ...f, preferred_language: v })}
                            placeholder={t("common.choose")}
                        />
                    </div>
                </div>
                <button data-testid="profile-save" onClick={save} disabled={savingProfile} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
                    <Save className="h-4 w-4" />
                    {savingProfile ? t("common.loading") : t("profile.save")}
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <ChangePasswordCard />
                <ChangeEmailCard currentEmail={profile.email} />
            </div>

            {!profile.is_organizer && (
                <div className="bento-card p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h3 className="font-heading font-bold text-xl">{t("profile.become_organizer_title")}</h3>
                        <p className="text-sm text-muted-foreground mt-1 max-w-md">
                            {t("profile.country_locked_hint")}
                        </p>
                    </div>
                    <Link to="/become-organizer" data-testid="become-organizer-btn" className="btn-primary inline-flex items-center gap-2 whitespace-nowrap">
                        <UserPlus className="h-4 w-4" />
                        {t("nav.become_organizer")}
                    </Link>
                </div>
            )}
        </div>
    );
}

const I = ({ label, v, set, tid }) => (
    <div>
        <label className="text-sm font-semibold block mb-1.5">{label}</label>
        <input data-testid={tid} type="text" value={v || ""} onChange={(e) => set(e.target.value)} className="input-modern" />
    </div>
);

// ============================== CHANGE PASSWORD ==============================

const ChangePasswordCard = () => {
    const { t } = useTranslation();
    const { profile, installSession } = useAuth();
    const [open, setOpen] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [next2, setNext2] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const reset = () => { setCurrent(""); setNext(""); setNext2(""); setShow(false); };

    const initiate = async (e) => {
        e?.preventDefault?.();
        if (next.length < 6) return toast.error(t("auth.password_too_short"));
        if (next !== next2) return toast.error(t("auth.passwords_do_not_match"));
        setBusy(true);
        try {
            await api.post("/api/auth/password-change/initiate", { current_password: current });
            setOpen(false);
            setVerifyOpen(true);
            toast.success(t("auth.code_sent"));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) toast.error(t("profile.current_password_incorrect"));
            else if (status === 429) toast.error(t("auth.too_many_requests"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    const confirm = async (code) => {
        const { data } = await api.post("/api/auth/password-change/confirm", { code, new_password: next });
        return data;
    };

    const onSuccess = async (data) => {
        if (data?.access_token) {
            await installSession(data);
        }
        reset();
    };

    return (
        <>
            <div className="bento-card p-5 space-y-3">
                <h3 className="font-bold flex items-center gap-2"><KeyRound className="h-4 w-4" /> {t("profile.change_password")}</h3>
                <p className="text-sm text-muted-foreground">{t("profile.change_password_hint")}</p>
                <button data-testid="change-password-open" onClick={() => setOpen(true)} className="btn-outline w-full">
                    {t("profile.change_password")}
                </button>
            </div>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
                <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-black text-2xl">{t("profile.change_password")}</DialogTitle>
                        <DialogDescription>{t("profile.change_password_subtitle")}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={initiate} className="space-y-4 pt-2">
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("profile.current_password")}</label>
                            <div className="relative">
                                <input data-testid="cp-current" type={show ? "text" : "password"} required value={current}
                                    onChange={(e) => setCurrent(e.target.value)} className="input-modern pr-12" />
                                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("auth.new_password")}</label>
                            <input data-testid="cp-new" type={show ? "text" : "password"} required minLength={6}
                                value={next} onChange={(e) => setNext(e.target.value)} className="input-modern" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("auth.confirm_password")}</label>
                            <input data-testid="cp-new2" type={show ? "text" : "password"} required minLength={6}
                                value={next2} onChange={(e) => setNext2(e.target.value)} className="input-modern" />
                        </div>
                        <button type="submit" data-testid="cp-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
                            {busy ? t("common.loading") : t("auth.send_code")}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <VerifyCodeDialog
                open={verifyOpen}
                onOpenChange={setVerifyOpen}
                email={profile?.email}
                onSubmit={confirm}
                onSuccess={onSuccess}
                title={t("profile.change_password")}
                description={t("auth.enter_code_subtitle", { email: profile?.email || "" })}
                testId="cp-verify"
            />
        </>
    );
};

// ============================== CHANGE EMAIL ==============================

const ChangeEmailCard = ({ currentEmail }) => {
    const { t } = useTranslation();
    const { refreshProfile, installSession } = useAuth();
    const [open, setOpen] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    const reset = () => { setNewEmail(""); setCurrentPassword(""); setShow(false); };

    const initiate = async (e) => {
        e?.preventDefault?.();
        if (!newEmail) return;
        setBusy(true);
        try {
            await api.post("/api/auth/email-change/initiate", { new_email: newEmail, current_password: currentPassword });
            setOpen(false);
            setVerifyOpen(true);
            toast.success(t("auth.code_sent_to_new"));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) toast.error(t("profile.current_password_incorrect"));
            else if (status === 409) toast.error(t("auth.email_already_exists"));
            else if (status === 400) toast.error(t("profile.same_email"));
            else if (status === 429) toast.error(t("auth.too_many_requests"));
            else toast.error(t("common.error"));
        } finally {
            setBusy(false);
        }
    };

    const confirm = async (code) => {
        const { data } = await api.post("/api/auth/email-change/confirm", { new_email: newEmail, code });
        return data;
    };

    const onSuccess = async (data) => {
        if (data?.access_token) {
            await installSession(data);
        } else {
            await refreshProfile();
        }
        reset();
    };

    return (
        <>
            <div className="bento-card p-5 space-y-3">
                <h3 className="font-bold flex items-center gap-2"><Mail className="h-4 w-4" /> {t("profile.change_email")}</h3>
                <p className="text-sm text-muted-foreground break-all">{currentEmail}</p>
                <button data-testid="change-email-open" onClick={() => setOpen(true)} className="btn-outline w-full">
                    {t("profile.change_email")}
                </button>
            </div>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
                <DialogContent className="sm:max-w-md" data-testid="change-email-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading font-black text-2xl">{t("profile.change_email")}</DialogTitle>
                        <DialogDescription>{t("profile.change_email_subtitle")}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={initiate} className="space-y-4 pt-2">
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("profile.new_email")}</label>
                            <input data-testid="ce-new" type="email" required value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)} className="input-modern"
                                placeholder="new@email.com" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("profile.current_password")}</label>
                            <div className="relative">
                                <input data-testid="ce-current-pwd" type={show ? "text" : "password"} required
                                    value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="input-modern pr-12" />
                                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>
                        <button type="submit" data-testid="ce-submit" disabled={busy} className="btn-primary w-full disabled:opacity-50">
                            {busy ? t("common.loading") : t("auth.send_code")}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <VerifyCodeDialog
                open={verifyOpen}
                onOpenChange={setVerifyOpen}
                email={newEmail}
                onSubmit={confirm}
                onSuccess={onSuccess}
                title={t("profile.change_email")}
                description={t("auth.enter_code_subtitle", { email: newEmail })}
                testId="ce-verify"
            />
        </>
    );
};
